import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { invalidateProfileViewCache } from '../../../utils/profileViewCache';
import './AccountSettings.css';

function DeactivateAccountForm({ onClose }) {
  const { t } = useTranslation(['profile', 'shared']);
  const [reason, setReason] = useState('');
  const [customFeedback, setCustomFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const reasons = [
    'not_using',
    'better_alternative',
    'technical_issues',
    'no_time',
    'other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!reason) {
      setError(t('profile.settings.pages.account.deactivateForm.errorNoReason'));
      return;
    }

    if (reason === 'other' && !customFeedback.trim()) {
      setError(t('profile.settings.pages.account.deactivateForm.errorNoCustomFeedback'));
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error(t('profile.settings.pages.account.deactivateForm.errorNotLoggedIn'));
      }

      // 1. Zapisanie ankiety do account_actions
      const { error: insertError } = await supabase
        .from('account_actions')
        .insert({
          user_id: user.id,
          action_type: 'deactivate',
          reason: reason,
          feedback: reason === 'other' ? customFeedback.trim() : null
        });

      if (insertError) {
        throw insertError;
      }

      // 2. Aktualizacja is_active = false
      const { error: updateError } = await supabase
        .from('users')
        .update({ 
          is_active: false,
          deactivated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      invalidateProfileViewCache(user.id);

      // 3. Wylogowanie użytkownika
      await supabase.auth.signOut();
      
      // Przekierowanie do strony logowania
      window.location.href = '/';
    } catch (err) {
      console.error('Error deactivating account:', err);
      setError(err.message || t('profile.settings.pages.account.deactivateForm.errorDeactivate'));
      setShowConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  if (showConfirm) {
    return (
      <div className="account-form-container">
        <h3 className="account-form-title">
          {t('profile.settings.pages.account.deactivateForm.confirmTitle')}
        </h3>

        <div className="account-form-confirm-message">
          <p>{t('profile.settings.pages.account.deactivateForm.confirmMessage')}</p>
        </div>

        {error && (
          <div className="account-form-error">
            {error}
          </div>
        )}

        <div className="account-form-actions">
          <button
            type="button"
            onClick={() => setShowConfirm(false)}
            className="account-form-button account-form-button-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="account-form-button account-form-button-warning"
            disabled={loading}
          >
            {loading
              ? t('profile.settings.pages.account.deactivateForm.deactivating')
              : t('profile.settings.pages.account.deactivateForm.confirmButton')
            }
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-form-container">
      <h3 className="account-form-title">
        {t('profile.settings.pages.account.deactivateForm.title')}
      </h3>

      <form onSubmit={handleSubmit} className="account-form">
        <div className="account-form-field">
          <label className="account-form-label">
            {t('profile.settings.pages.account.deactivateForm.reasonLabel')}
          </label>
          <div className="account-form-radio-group">
            {reasons.map((reasonKey) => (
              <label key={reasonKey} className="account-form-radio">
                <input
                  type="radio"
                  name="reason"
                  value={reasonKey}
                  checked={reason === reasonKey}
                  onChange={(e) => setReason(e.target.value)}
                />
                <span>{t(`profile.settings.pages.account.deactivateForm.reasons.${reasonKey}`)}</span>
              </label>
            ))}
          </div>
        </div>

        {reason === 'other' && (
          <div className="account-form-field">
            <label htmlFor="customFeedback" className="account-form-label">
              {t('profile.settings.pages.account.deactivateForm.customFeedbackLabel')}
            </label>
            <textarea
              id="customFeedback"
              value={customFeedback}
              onChange={(e) => setCustomFeedback(e.target.value)}
              placeholder={t('profile.settings.pages.account.deactivateForm.customFeedbackPlaceholder')}
              className="account-form-textarea"
              rows={4}
            />
          </div>
        )}

        {error && (
          <div className="account-form-error">
            {error}
          </div>
        )}

        <div className="account-form-info">
          {t('profile.settings.pages.account.deactivateForm.info')}
        </div>

        <div className="account-form-actions">
          <button
            type="button"
            onClick={onClose}
            className="account-form-button account-form-button-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="account-form-button account-form-button-warning"
            disabled={loading || !reason}
          >
            {t('profile.settings.pages.account.deactivateForm.submitButton')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DeactivateAccountForm;





